import React, { useState } from "react";
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  WarningAmber as WarningAmberIcon,
  KeyboardArrowRight as ChevronIcon,
} from "@mui/icons-material";
import { SvgIconComponent } from "@mui/icons-material";
import { fonts, colors } from "../../styles/theme";
import { HEADER_HEIGHT, SIDEBAR_WIDTH } from "./layoutSizes";

export { SIDEBAR_WIDTH };

/** Untergeordnete Seite eines Moduls (zweite Navigationsebene) */
export interface SidebarPage {
  label: string;
  index: number;
  /** Eindeutiger Schlüssel für Warning-Lookup (z.B. PageType im Projekt-Kontext) */
  key: string;
  /** Steht da, ist aber noch nicht wählbar – grau, mit dem Grund im Tooltip */
  disabled?: boolean;
}

export interface SidebarItem {
  label: string;
  icon: SvgIconComponent;
  /**
   * Untergeordnete Seiten. Ab zwei Einträgen wird der Eintrag zum aufklappbaren Modul; bei genau einer Seite bleibt er ein einfacher
   * Eintrag und die Seite wird nicht separat angezeigt. Alternativ index/key direkt setzen
   * (flache Navigation).
   */
  pages?: SidebarPage[];
  index?: number;
  /** Eindeutiger Schlüssel für Warning-Lookup (z.B. PageType im Projekt-Kontext) */
  key?: string;
  disabled?: boolean;
}

/** Eintrag ohne untergeordnete Seiten - index und key sind hier verpflichtend */
export type SidebarFlatItem = SidebarItem & { index: number; key: string };

export interface SidebarSegment {
  /** Wird als Gruppen-Überschrift angezeigt - leer lassen für ungruppierte Navigation */
  label?: string;
  items: SidebarItem[];
}

interface SidebarProps {
  segments: SidebarSegment[];
  currentTab: number;
  onTabChange: (tabIndex: number) => void;
  hasWarning?: (key: string) => boolean;
}

const WARNING_TOOLTIP = "Es gibt Warnungen auf dieser Seite";
const MODULE_WARNING_TOOLTIP = "Es gibt Warnungen in diesem Modul";
const DISABLED_TOOLTIP = "Noch nicht verfügbar";

/**
 * Ein Wechsel des aktiven Eintrags blendet Kachel, Icon, Text und Führungslinie
 * gemeinsam über - gleiche Dauer, gleiche Kurve, damit es als eine Bewegung wirkt.
 *
 * Bewusst nur Farben: `font-weight` lässt sich nicht interpolieren und würde mitten
 * in der Überblendung springen. Die Schriftstärke bleibt deshalb konstant, die
 * Hervorhebung tragen Kachel und Textfarbe.
 */
const STATE_TRANSITION =
  "background-color 240ms ease, border-color 240ms ease, color 240ms ease";

/** Strichstärke der Führungslinie – gleich dem linken Rand der Seiten */
const LINIE_PX = 2;

/** Warndreieck neben einem Navigations-Label */
const WarningMarker: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} arrow>
    <WarningAmberIcon
      sx={{
        color: colors.state.highlight,
        fontSize: "1rem",
        cursor: "help",
      }}
    />
  </Tooltip>
);

/**
 * Generische Sidebar-Navigation mit Segment-Gruppierung.
 * Wird sowohl für die Projekt-Navigation (ProjectSidebar) als auch für die
 * Admin-Organisations-Navigation (AdminOrgDetailPage) verwendet.
 *
 * Zwei Ebenen: Modul (oben) und Seite (darunter). Module klappen unabhängig voneinander auf
 * und zu - ein Klick betrifft nur das geklickte Modul, beliebig viele bleiben offen. Das Modul
 * der aktuellen Seite ist per Default offen. Ein Modul mit mehreren Seiten wechselt selbst
 * nicht die Seite - das tut erst der Klick auf eine seiner Seiten.
 */
const Sidebar: React.FC<SidebarProps> = ({
  segments,
  currentTab,
  onTabChange,
  hasWarning,
}) => {
  const istAktiveSeite = (page: SidebarPage) =>
    !page.disabled && currentTab === page.index;

  const waehleSeite = (page: SidebarPage) => {
    if (!page.disabled) onTabChange(page.index);
  };

  // Label des Moduls, in dem die aktuelle Seite liegt
  const currentModuleLabel =
    segments
      .flatMap((segment) => segment.items)
      .find(
        (item) =>
          currentTab === item.index ||
          (item.pages ?? []).some(istAktiveSeite),
      )?.label ?? null;

  // Nur die Klicks des Nutzers werden gespeichert; ohne Klick ergibt sich der Zustand aus
  // der Route. So klappt ein Modul beim Klick sofort auf, ohne auf die Navigation zu warten.
  const [expandedOverrides, setExpandedOverrides] = useState<
    Record<string, boolean>
  >({});

  const isModuleExpanded = (label: string) =>
    expandedOverrides[label] ?? label === currentModuleLabel;

  const handleItemClick = (
    item: SidebarItem,
    isExpandable: boolean,
    targetPage: SidebarPage | undefined,
  ) => {
    if (item.disabled) return;

    // Ein Modul mit mehreren Seiten klappt nur auf und zu - der Seitenwechsel
    // passiert erst beim Klick auf eine seiner Seiten
    if (isExpandable) {
      setExpandedOverrides((overrides) => ({
        ...overrides,
        [item.label]: !isModuleExpanded(item.label),
      }));
      return;
    }

    if (targetPage) waehleSeite(targetPage);
    else if (item.index !== undefined) onTabChange(item.index);
  };

  /**
   * Eine Seite in der Liste unter ihrem Modul. Eine noch nicht wählbare Seite steht grau da; der
   * Tooltip sagt warum (am umgebenden Element, der deaktivierte Knopf selbst nimmt keine Maus an).
   */
  const renderSeite = (page: SidebarPage) => {
    const isActivePage = istAktiveSeite(page);
    const pageHasWarning = !page.disabled && (hasWarning?.(page.key) ?? false);

    const eintrag = (
      <ListItem key={`${page.key}-${page.index}`} disablePadding>
        <ListItemButton
          onClick={() => waehleSeite(page)}
          disabled={page.disabled}
          sx={{
            py: 0.75,
            pl: 2,
            borderRadius: 0,
            borderLeft: `${LINIE_PX}px solid ${
              isActivePage ? colors.primary.main : colors.border.light
            }`,
            transition: STATE_TRANSITION,
            "&:hover": {
              backgroundColor: colors.opacity.black04,
            },
            "&.Mui-disabled": {
              opacity: 1,
              pointerEvents: "none",
            },
          }}
        >
          <ListItemText
            primary={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                {page.label}
                {pageHasWarning && <WarningMarker title={WARNING_TOOLTIP} />}
              </Box>
            }
            sx={{
              my: 0,
              "& .MuiTypography-root": {
                fontWeight: fonts.weights.regular,
                fontSize: "0.85rem",
                color: isActivePage
                  ? colors.primary.main
                  : page.disabled
                    ? colors.text.disabled
                    : colors.text.secondary,
                transition: STATE_TRANSITION,
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    );
    return page.disabled ? (
      <Tooltip
        key={`${page.key}-${page.index}`}
        title={DISABLED_TOOLTIP}
        placement="right"
        arrow
      >
        <Box sx={{ cursor: "not-allowed" }}>{eintrag}</Box>
      </Tooltip>
    ) : (
      eintrag
    );
  };

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        backgroundColor: "transparent",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        left: 0,
        top: HEADER_HEIGHT,
        zIndex: 1000,
      }}
    >
      <Box sx={{ flex: 1, pt: 3, overflow: "auto" }}>
        {segments.map((segment, segmentIndex) => (
          <Box
            key={segment.label ?? segmentIndex}
            sx={{ mt: segmentIndex === 0 ? 0 : 2.5 }}
          >
            {segment.label && (
              <Typography
                sx={{
                  px: 3,
                  mb: 0.5,
                  fontSize: "0.65rem",
                  fontWeight: fonts.weights.semiBold,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: colors.text.secondary,
                }}
              >
                {segment.label}
              </Typography>
            )}
            <List disablePadding>
              {segment.items.map((item) => {
                const IconComponent = item.icon;
                const pages = item.pages ?? [];
                const isExpandable = pages.length > 1;

                // Modul mit genau einer Seite verhält sich wie ein flacher Eintrag:
                // Ziel und Warning-Schlüssel kommen dann von dieser Seite.
                const targetPage = isExpandable ? undefined : pages[0];
                const warningKeys = pages.length
                  ? pages.map((page) => page.key)
                  : item.key
                    ? [item.key]
                    : [];

                const isCurrent =
                  !item.disabled &&
                  ((item.index !== undefined && currentTab === item.index) ||
                    pages.some(istAktiveSeite));
                const itemHasWarning =
                  !item.disabled &&
                  warningKeys.some((key) => hasWarning?.(key) ?? false);
                const isExpanded = isExpandable && isModuleExpanded(item.label);

                return (
                  <Box key={item.label}>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() =>
                          handleItemClick(item, isExpandable, targetPage)
                        }
                        disabled={item.disabled}
                        aria-expanded={isExpandable ? isExpanded : undefined}
                        sx={{
                          mx: 2,
                          borderRadius: 2,
                          transition: STATE_TRANSITION,
                          backgroundColor: isCurrent
                            ? colors.opacity.primary08
                            : "transparent",
                          border: isCurrent
                            ? `1px solid ${colors.primary.main}20`
                            : "1px solid transparent",
                          "&:hover": {
                            backgroundColor: isCurrent
                              ? colors.opacity.primary08
                              : colors.opacity.black04,
                          },
                          "&.Mui-selected": {
                            backgroundColor: colors.opacity.primary08,
                          },
                          "&.Mui-disabled": {
                            opacity: 1,
                            pointerEvents: "none",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: isCurrent
                              ? colors.primary.main
                              : "text.secondary",
                            transition: STATE_TRANSITION,
                          }}
                        >
                          <IconComponent fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              {item.label}
                              {itemHasWarning && (
                                <WarningMarker
                                  title={
                                    isExpandable
                                      ? MODULE_WARNING_TOOLTIP
                                      : WARNING_TOOLTIP
                                  }
                                />
                              )}
                            </Box>
                          }
                          sx={{
                            "& .MuiTypography-root": {
                              fontWeight: fonts.weights.medium,
                              fontSize: "0.9rem",
                              color: isCurrent
                                ? colors.primary.main
                                : "text.primary",
                              transition: STATE_TRANSITION,
                            },
                          }}
                        />
                        {isExpandable && (
                          <ChevronIcon
                            sx={{
                              fontSize: "1.1rem",
                              color: isCurrent
                                ? colors.primary.main
                                : "text.secondary",
                              transform: isExpanded
                                ? "rotate(90deg)"
                                : "rotate(0deg)",
                              transition: `${STATE_TRANSITION}, transform 240ms ease`,
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>

                    {/* Collapse-Dauer wie Chevron-Drehung und STATE_TRANSITION, damit Pfeil
                        und Aufklappen als eine Bewegung wirken. Reduced Motion deckt die
                        globale Regel in index.css ab. */}
                    {isExpandable && (
                      <Collapse in={isExpanded} timeout={240} unmountOnExit>
                        {/* Führungslinie: entsteht aus den linken Rändern der Seiten,
                            deshalb liegen die Einträge ohne Abstand aufeinander */}
                        <List disablePadding sx={{ ml: 4.5, mr: 2, mb: 0.5 }}>
                          {pages.map((page) => renderSeite(page))}
                        </List>
                      </Collapse>
                    )}
                  </Box>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Sidebar;
